import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  SelectedDeliveriesList,
  SelectedDeliveryItem,
} from "app/components/Shared/SelectedDeliveriesList";
import { getRouteDisplayLabel } from "app/utils/odooDisplay";

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  maxWidth: "92vw",
  maxHeight: "90vh",
  overflowY: "auto" as const,
  bgcolor: "background.paper",
  border: "1px solid #e3e6ee",
  boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
  p: 4,
  borderRadius: "10px",
};

export type RouteMode = "existing" | "create_new";

export type AssignRouteFormValues = {
  mode: RouteMode;
  routeId: number | "";
  driverId: number | "";
  date: Dayjs | null;
  startTime: string;
  finishTime: string;
  depotId: number | "";
  isRoundTrip: boolean;
};

type OptionItem = {
  value: number;
  label: string;
};

interface AssignRouteModalProps {
  openModal: boolean;
  isSubmitting?: boolean;
  isRefreshing?: boolean;
  routes: Array<{
    id: number;
    name: string;
    display_name?: string;
    state?: string;
    picking_count?: number;
    total_load_count?: number;
    stop_line_ids?: Array<{ load_count?: number }>;
  }>;
  modalTitle?: string;
  submitLabel?: string;
  submittingLabel?: string;
  drivers: Array<{ id: number; name: string }>;
  depots?: Array<{ id: number; name: string }>;
  selectedOrders?: SelectedDeliveryItem[];
  userTimezone?: string;
  onRefresh?: () => Promise<void>;
  handleClose: (event?: object, reason?: string) => void;
  handleAssignRoute: (values: AssignRouteFormValues) => Promise<void>;
}

const defaultValues: AssignRouteFormValues = {
  mode: "existing",
  routeId: "",
  driverId: "",
  date: dayjs(),
  startTime: "",
  finishTime: "",
  depotId: "",
  isRoundTrip: false,
};

const hasCompleteTime = (value: string): boolean =>
  /^\d{2}:\d{2}$/.test(value.trim());

const normalizeTimeValue = (value: string): string => {
  const trimmed = value.trim();
  return hasCompleteTime(trimmed) ? trimmed : "";
};

const isValidOptionalTimeRange = (
  startTime: string,
  finishTime: string
): boolean => {
  const start = normalizeTimeValue(startTime);
  const finish = normalizeTimeValue(finishTime);

  if (!start || !finish) {
    return true;
  }

  return finish > start;
};

const hasTimeRangeError = (startTime: string, finishTime: string): boolean =>
  !isValidOptionalTimeRange(startTime, finishTime);

export default function AssignRouteModal(props: AssignRouteModalProps) {
  const {
    openModal,
    isSubmitting = false,
    isRefreshing = false,
    routes,
    drivers,
    depots = [],
    selectedOrders = [],
    userTimezone = "Asia/Kolkata",
    modalTitle = "Assign Route",
    submitLabel = "Assign Route",
    submittingLabel = "Assigning route...",
    onRefresh,
    handleClose,
    handleAssignRoute,
  } = props;

  const isBusy = isSubmitting || isRefreshing;

  const [formValues, setFormValues] =
    React.useState<AssignRouteFormValues>(defaultValues);
  const [validationError, setValidationError] = React.useState("");

  const routeOptions = React.useMemo<OptionItem[]>(
    () =>
      routes.map((route) => ({
        value: route.id,
        label: getRouteDisplayLabel(route),
      })),
    [routes]
  );

  const driverOptions = React.useMemo<OptionItem[]>(
    () =>
      drivers.map((driver) => ({
        value: driver.id,
        label: driver.name,
      })),
    [drivers]
  );

  const depotOptions = React.useMemo<OptionItem[]>(
    () =>
      depots.map((depot) => ({
        value: depot.id,
        label: depot.name,
      })),
    [depots]
  );

  React.useEffect(() => {
    if (!openModal) {
      setFormValues(defaultValues);
      setValidationError("");
    }
  }, [openModal]);

  React.useEffect(() => {
    if (
      !hasTimeRangeError(formValues.startTime, formValues.finishTime) &&
      validationError === "Finish time must be after start time."
    ) {
      setValidationError("");
    }
  }, [formValues.startTime, formValues.finishTime, validationError]);

  const handleSave = async () => {
    if (isSubmitting) {
      return;
    }

    if (formValues.driverId === "") {
      setValidationError("Please select a driver.");
      return;
    }

    if (formValues.mode === "existing" && formValues.routeId === "") {
      setValidationError("Please select a route.");
      return;
    }

    if (formValues.mode === "create_new" && !formValues.date) {
      setValidationError("Please select a date for the new route.");
      return;
    }

    if (
      formValues.mode === "create_new" &&
      hasTimeRangeError(formValues.startTime, formValues.finishTime)
    ) {
      setValidationError("Finish time must be after start time.");
      return;
    }

    setValidationError("");
    await handleAssignRoute({
      ...formValues,
      startTime: normalizeTimeValue(formValues.startTime),
      finishTime: normalizeTimeValue(formValues.finishTime),
    });
  };

  return (
    <Modal
      open={openModal}
      disableEscapeKeyDown={isBusy}
      onClose={handleClose}
      aria-labelledby="assign-route-modal-title"
    >
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            gap: 1,
          }}
        >
          <Typography
            id="assign-route-modal-title"
            sx={{ fontSize: "24px", fontWeight: "bold", flex: 1 }}
          >
            {modalTitle}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {onRefresh && (
              <IconButton
                aria-label="Refresh routes and partners"
                disabled={isBusy}
                title="Refresh routes, drivers, and depots"
                onClick={() => {
                  void onRefresh();
                }}
              >
                {isRefreshing ? (
                  <CircularProgress size={22} sx={{ color: "#5fa67a" }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            )}
            <IconButton
              disabled={isBusy}
              sx={{ fontSize: "32px" }}
              onClick={() => handleClose()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </Box>

        {selectedOrders.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <SelectedDeliveriesList
              items={selectedOrders}
              userTimezone={userTimezone}
              defaultOpen
            />
          </Box>
        )}

        <FormControl component="fieldset" sx={{ width: "100%", mb: 2 }}>
          <FormLabel component="legend" sx={{ mb: 1, color: "#625a57" }}>
            Route option
          </FormLabel>
          <RadioGroup
            value={formValues.mode}
            onChange={(event) => {
              setValidationError("");
              setFormValues((prev) => ({
                ...prev,
                mode: event.target.value as RouteMode,
              }));
            }}
          >
            <FormControlLabel
              value="existing"
              control={<Radio disabled={isBusy} />}
              label="Select existing route"
            />
            <FormControlLabel
              value="create_new"
              control={<Radio disabled={isBusy} />}
              label="Create new route"
            />
          </RadioGroup>
        </FormControl>

        {formValues.mode === "existing" ? (
          <Autocomplete
            disablePortal
            disabled={isBusy}
            options={routeOptions}
            sx={{ width: "100%", mb: 2 }}
            renderInput={(params) => (
              <TextField {...params} label="Route" required />
            )}
            onChange={(_event, value) => {
              setValidationError("");
              setFormValues((prev) => ({
                ...prev,
                routeId: value?.value ?? "",
              }));
            }}
          />
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
              <DatePicker
                label="Route date"
                format="MM/DD/YYYY"
                value={formValues.date}
                disabled={isBusy}
                onChange={(value) => {
                  setValidationError("");
                  setFormValues((prev) => ({ ...prev, date: value }));
                }}
                slotProps={{
                  textField: { fullWidth: true, required: true },
                }}
              />
              <TextField
                label="Start time"
                type="time"
                value={formValues.startTime}
                disabled={isBusy}
                onChange={(event) => {
                  setValidationError("");
                  setFormValues((prev) => ({
                    ...prev,
                    startTime: event.target.value,
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                fullWidth
              />
              <TextField
                label="Finish time"
                type="time"
                value={formValues.finishTime}
                disabled={isBusy}
                onChange={(event) => {
                  setValidationError("");
                  setFormValues((prev) => ({
                    ...prev,
                    finishTime: event.target.value,
                  }));
                }}
                error={hasTimeRangeError(
                  formValues.startTime,
                  formValues.finishTime
                )}
                helperText={
                  hasTimeRangeError(formValues.startTime, formValues.finishTime)
                    ? "Finish time must be after start time."
                    : undefined
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                fullWidth
              />
              <Autocomplete
                disablePortal
                disabled={isBusy}
                options={depotOptions}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField {...params} label="Depot address (optional)" />
                )}
                onChange={(_event, value) => {
                  setValidationError("");
                  setFormValues((prev) => ({
                    ...prev,
                    depotId: value?.value ?? "",
                  }));
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.isRoundTrip}
                    disabled={isBusy}
                    onChange={(event) => {
                      setFormValues((prev) => ({
                        ...prev,
                        isRoundTrip: event.target.checked,
                      }));
                    }}
                    sx={{
                      color: "#9ca3af",
                      "&.Mui-checked": { color: "#5fa67a" },
                    }}
                  />
                }
                label="Round trip"
              />
            </Box>
          </LocalizationProvider>
        )}

        <Autocomplete
          disablePortal
          disabled={isBusy}
          options={driverOptions}
          sx={{ width: "100%", mb: 2 }}
          renderInput={(params) => (
            <TextField {...params} label="Driver" required />
          )}
          onChange={(_event, value) => {
            setValidationError("");
            setFormValues((prev) => ({
              ...prev,
              driverId: value?.value ?? "",
            }));
          }}
        />

        <Button
          variant="contained"
          disabled={isBusy}
          sx={{
            width: "100%",
            backgroundColor: "#7ec597",
            color: "#000",
            fontSize: "20px",
            minHeight: "48px",
            textTransform: "none",
            ":hover": { backgroundColor: "#6db486" },
            ":disabled": { backgroundColor: "#b8dcc6", color: "#4a4a4a" },
          }}
          onClick={handleSave}
        >
          {isSubmitting ? (
            <Box display="flex" alignItems="center" gap={1.5}>
              <CircularProgress size={22} sx={{ color: "#2f5f3f" }} />
              {submittingLabel}
            </Box>
          ) : (
            submitLabel
          )}
        </Button>

        {validationError && (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "bold",
              textAlign: "center",
              color: "red",
              marginTop: "10px",
            }}
          >
            {validationError}
          </Typography>
        )}
      </Box>
    </Modal>
  );
}
